import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TeamMember } from '@dmn/shared';
import { Mail, ShieldCheck, UserPlus, Users } from 'lucide-react';
import { PageBody, PageHeader } from '@/components/layout/PageHeader';
import { EmptyState, ErrorState, LoadingRows } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TablePagination } from '@/components/TablePagination';
import { usePagination } from '@/hooks/use-pagination';
import { TableSearch } from '@/components/TableSearch';
import { useTableSearch } from '@/hooks/use-table-search';
import { api, ApiError } from '@/lib/api';
import { queryKeys } from '@/lib/queries';
import { formatDateTime } from '@/lib/utils';

export const Route = createFileRoute('/team/')({ component: TeamIndex });

/**
 * Who can use the console.
 *
 * Lists every Google account that has ever signed in, not only the admins —
 * because the question this page gets opened for is "why can't so-and-so get
 * in", and that person's account exists with no `dmn.admin` claim. A list of
 * admins only would leave them invisible and the answer unguessable.
 */
function TeamIndex() {
  const queryClient = useQueryClient();
  const team = useQuery({ queryKey: queryKeys.team, queryFn: api.team.list });

  const [inviteEmail, setInviteEmail] = useState('');

  const invite = useMutation({
    mutationFn: (email: string) => api.team.invite({ email }),
    onSuccess: async () => {
      setInviteEmail('');
      await queryClient.invalidateQueries({ queryKey: queryKeys.team });
    },
  });

  const setAdmin = useMutation({
    mutationFn: ({ uid, isAdmin }: { uid: string; isAdmin: boolean }) =>
      api.team.setAdmin(uid, { isAdmin }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.team });
    },
  });

  const members = team.data?.members ?? [];
  const admins = members.filter((member) => member.isAdmin).length;

  const search = useTableSearch(members, (m) => [m.email, m.displayName, m.uid, m.accessSource]);
  const pagination = usePagination(search.filtered);
  return (
    <>
      <PageHeader
        title="Team"
        description={
          team.data
            ? `${admins} with console access of ${members.length} account${members.length === 1 ? '' : 's'}`
            : 'Console access'
        }
        actions={
          <form
            className="flex items-center gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              const email = inviteEmail.trim();
              if (email) invite.mutate(email);
            }}
          >
            <Input
              type="email"
              value={inviteEmail}
              onChange={(event) => setInviteEmail(event.target.value)}
              placeholder="name@getdeardoc.com"
              className="h-9 w-[240px]"
              aria-label="Email address to invite"
            />
            <Button type="submit" size="sm" disabled={invite.isPending || !inviteEmail.trim()}>
              <UserPlus className="size-4" />
              Invite
            </Button>
          </form>
        }
      />
      <PageBody>
        <TableSearch
          search={search}
          placeholder="Search name or email"
          label="Search team"
          noun="account"
        />
        {invite.isError && (
          <p className="text-sm text-destructive">
            {invite.error instanceof ApiError ? invite.error.message : 'Could not invite'}
          </p>
        )}
        {invite.isSuccess && (
          <p className="text-sm text-emerald-600">
            {invite.data.created
              ? `${invite.data.member.email} can sign in now — no email was sent, so tell them yourself.`
              : `${invite.data.member.email} already had an account and now has access.`}
          </p>
        )}

        <Card className="shadow-none">
          <CardContent className="p-0">
            {team.isError ? (
              <ErrorState error={team.error} onRetry={() => void team.refetch()} />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Person</TableHead>
                    <TableHead>Access</TableHead>
                    <TableHead>Last sign-in</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {team.isPending && <LoadingRows columns={4} />}
                  {pagination.pageRows.map((member) => (
                    <TableRow key={member.uid}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{member.displayName ?? member.email}</span>
                          {member.isSelf && (
                            <Badge variant="secondary" className="text-xs">
                              You
                            </Badge>
                          )}
                          {member.pendingInvite && (
                            <Badge variant="outline" className="text-xs text-amber-600">
                              <Mail className="mr-1 size-3" />
                              Invited
                            </Badge>
                          )}
                          {member.disabled && (
                            <Badge variant="outline" className="text-xs text-muted-foreground">
                              Disabled
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">{member.email}</p>
                      </TableCell>
                      <TableCell>
                        <AccessBadge member={member} />
                      </TableCell>
                      <TableCell className="text-sm whitespace-nowrap text-muted-foreground">
                        {member.lastSignInAt
                          ? formatDateTime(member.lastSignInAt, 'MMM D, YYYY')
                          : 'Never'}
                      </TableCell>
                      <TableCell className="text-right">
                        <MemberAction
                          member={member}
                          pending={setAdmin.isPending}
                          onChange={(isAdmin) => setAdmin.mutate({ uid: member.uid, isAdmin })}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
            <TablePagination pagination={pagination} label="accounts" />
            {members.length === 0 && !team.isPending && !team.isError && (
              <EmptyState
                title="No one here yet"
                description="Invite a colleague by email above — they will have access the moment they first sign in with Google."
                icon={Users}
              />
            )}
          </CardContent>
        </Card>

        {setAdmin.isError && (
          <p className="text-sm text-destructive">
            {setAdmin.error instanceof ApiError
              ? setAdmin.error.message
              : 'Could not change access'}
          </p>
        )}
      </PageBody>
    </>
  );
}

function AccessBadge({ member }: { member: TeamMember }) {
  if (member.accessSource === 'claim') {
    return (
      <Badge className="bg-emerald-600 hover:bg-emerald-600">
        <ShieldCheck className="mr-1 size-3" />
        Admin
      </Badge>
    );
  }
  if (member.accessSource === 'bootstrap') {
    return (
      <Badge variant="secondary">
        Admin
        <span className="ml-1 font-normal opacity-70">· bootstrap</span>
      </Badge>
    );
  }
  if (member.accessSource === 'sponsor') {
    // Read-only here on purpose — the lever lives on the sponsor
    // (`/sponsors`), not here, so this row cannot grant or revoke anything.
    return (
      <Badge variant="outline">
        Sponsor
        {member.sponsorName && (
          <span className="ml-1 font-normal opacity-70">· {member.sponsorName}</span>
        )}
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-muted-foreground">
      No access
    </Badge>
  );
}

function MemberAction({
  member,
  pending,
  onChange,
}: {
  member: TeamMember;
  pending: boolean;
  onChange: (isAdmin: boolean) => void;
}) {
  // Both guards are enforced server-side too; these only keep the UI from
  // offering an action that would be refused.
  if (member.isSelf) return <span className="text-xs text-muted-foreground">—</span>;

  if (member.accessSource === 'bootstrap') {
    return <span className="text-xs text-muted-foreground">Set in config</span>;
  }

  if (member.accessSource === 'sponsor') {
    // One lever, one place: invite/revoke live on the sponsor
    // (`/sponsors`), so this list never offers a second way to change it.
    return <span className="text-xs text-muted-foreground">Managed on the sponsor</span>;
  }

  const granting = !member.isAdmin;

  return (
    <Button
      size="sm"
      variant={granting ? 'default' : 'outline'}
      disabled={pending}
      onClick={() => {
        if (granting || window.confirm(`Remove console access for ${member.email}?`)) {
          onChange(granting);
        }
      }}
    >
      {granting ? 'Grant access' : 'Revoke'}
    </Button>
  );
}
